import { Box } from '@mui/material';
import axios from 'axios';
import React, { CSSProperties, RefObject, VideoHTMLAttributes, useEffect, useState } from 'react';

import { EventDetails } from '../../../backendsdk';
import useCallbackRef from '../../../hooks/callbackRef';
import { DEFAULT_STREAMS, VideoStreamType, geussStream, streamLocation } from '../../../utils/ImageDimensions';

export type LayoutOptions = '1x1' | '1x1+3x1' | '2x1' | '1x2';

interface StreamsGridDef {
    grid: {
        gridTemplateRows: CSSProperties['gridTemplateRows'];
        gridTemplateColumns: CSSProperties['gridTemplateColumns'];
        gridTemplateAreas: CSSProperties['gridTemplateAreas'];
        overflow?: CSSProperties['overflow'];
    };
    mainStreamWidth: CallableFunction;
    secondaryStreamWidth: CallableFunction;
}

const fitContainer = (targetRef: RefObject<HTMLDivElement>, downscale = 1) =>
    targetRef.current ? targetRef.current.clientWidth / downscale : `${100 / downscale}%`;
const fullWidth = () => '100%';

export const STREAMS_GRID_DEFS: Record<string, StreamsGridDef> = {
    '1x1+3x1+controls': {
        grid: {
            gridTemplateRows: 'repeat(3, 1fr) 40px',
            gridTemplateColumns: 'repeat(4, 25%)',
            gridTemplateAreas: `"secondary0 main main main"
"secondary1 main main main"
"secondary2 main main main"
"controls controls controls controls"`,
            overflow: 'hidden',
        },
        mainStreamWidth: (ref: RefObject<HTMLDivElement>) => fitContainer(ref, 4 / 3),
        secondaryStreamWidth: fullWidth,
    },
    '1x1+3x1': {
        grid: {
            gridTemplateRows: 'repeat(3, 1fr)', // Always 3 rows of equal height - each secondary image gets 1/3 of main video height
            gridTemplateColumns: '1fr 3fr', // Left column for secondaries, right column for main
            gridTemplateAreas: `"secondary0 main"
"secondary1 main"
"secondary2 main"`, // All 3 rows for 3 streams
        },
        mainStreamWidth: (ref: RefObject<HTMLDivElement>) => fitContainer(ref, 4 / 3),
        secondaryStreamWidth: fullWidth,
    },
    // original
    '2x1': {
        grid: {
            gridTemplateRows: '1fr',
            gridTemplateColumns: 'repeat(2, 50%)',
            gridTemplateAreas: `"main secondary0"`,
        },
        mainStreamWidth: fullWidth,
        secondaryStreamWidth: fullWidth,
    },
    '2x1+controls': {
        grid: {
            gridTemplateRows: '1fr 40px',
            gridTemplateColumns: 'repeat(2, 50%)',
            gridTemplateAreas: `"main secondary0"
"controls controls"`,
        },
        mainStreamWidth: fullWidth,
        secondaryStreamWidth: fullWidth,
    },
    // feed
    '1x2': {
        grid: {
            gridTemplateRows: '57% 44%', // avoid overlap ic 4/3 - ff 16/9
            gridTemplateColumns: '1fr',
            gridTemplateAreas: `"main"
"secondary0"`,
        },

        mainStreamWidth: fitContainer,
        secondaryStreamWidth: fitContainer,
    },
    '1x2+controls': {
        grid: {
            gridTemplateRows: '52% 44% 40px', // avoid overlap ic 4/3 - ff 16/9
            gridTemplateColumns: '1fr',
            gridTemplateAreas: `"main"
"secondary0"
"controls"`,
        },

        mainStreamWidth: fitContainer,
        secondaryStreamWidth: fitContainer,
    },
    // efficient mode
    '1x1': {
        grid: {
            gridTemplateRows: '1fr',
            gridTemplateColumns: '1fr',
            gridTemplateAreas: `"main"`,
        },

        mainStreamWidth: fitContainer,
        secondaryStreamWidth: fitContainer,
    },
    '1x1+controls': {
        grid: {
            gridTemplateRows: '1fr 40px',
            gridTemplateColumns: '1fr',
            gridTemplateAreas: `"main"
"controls"`,
        },

        mainStreamWidth: fitContainer,
        secondaryStreamWidth: fitContainer,
    },
};

export const getVideoStreams = (eventDetails: EventDetails, callback: CallableFunction) => {
    const videoWriterKey = Object.keys(eventDetails.artifact_path).find(
        (key) => key.endsWith('video_writer.csv') && !key.endsWith('extended_frame_video_writer.csv'),
    );
    if (videoWriterKey) {
        axios.get(eventDetails.artifact_path[videoWriterKey]).then((response) => callback(response.data));
    }
};

const VIDEO_MARGIN = 2; // px

type CroppedVideoProps = {
    id: string;
    src: string;
    stream: VideoStreamType;
    streams: Record<VideoStreamType, streamLocation>;
    videoRef?: (newNode: HTMLVideoElement | null) => void;
    videoNode?: HTMLVideoElement | null;
    videoProps: VideoHTMLAttributes<HTMLVideoElement>;
    applyBorderRadius?: boolean;
    applyXScale?: boolean;
    applyHover?: boolean;
};

const CroppedVideo: React.FC<CroppedVideoProps> = (props) => {
    const [ref, node] = useCallbackRef<HTMLDivElement>();
    const [calculateCrop, setCalculatedCrop] = useState<streamLocation | undefined>();
    const [scale, setScale] = useState(1);
    const crop = props.streams?.[props.stream] || calculateCrop || DEFAULT_STREAMS[props.stream];

    useEffect(() => {
        // resize image for client
        if (!node || !crop) return;
        const resizeObserver = new ResizeObserver(() => {
            const wrapperWidth = node.clientWidth;
            const wrapperHeight = node.clientHeight;

            const scaleX = wrapperWidth / crop[2];
            const scaleY = wrapperHeight / crop[3];

            setScale(Math.min(scaleX, scaleY));
        });
        resizeObserver.observe(node);
    }, [node?.clientHeight, node?.clientWidth, crop]);

    useEffect(() => {
        // chose stream based on video size
        if (
            (!props.streams || !props.streams[props.stream]) &&
            props.videoNode?.videoHeight &&
            props.videoNode?.videoWidth
        ) {
            setCalculatedCrop(geussStream(props.stream, props.videoNode?.videoWidth, props.videoNode?.videoHeight));
        }
    }, [props.stream, props.videoNode?.videoHeight, props.videoNode?.videoWidth]);

    const [left, top, width, height] = !!props.streams?.[props.stream]
        ? props.streams[props.stream]
        : crop || DEFAULT_STREAMS[props.stream];

    if (!crop) {
        return (
            <video
                id={props.id}
                ref={props.videoRef}
                src={props.src}
                {...props.videoProps}
                style={{ width: 0, height: 0 }}
                onLoadedData={(event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
                    const element = event.target as HTMLVideoElement;
                    setCalculatedCrop(geussStream(props.stream, element.videoWidth, element.videoHeight));
                }}
            />
        );
    }

    const scaleX = props.applyXScale ? scale * (16 / 9 / (width / height)) : scale;

    return (
        <Box
            id={`${props.stream}-cropped-video-container`}
            ref={ref}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box id={`${props.stream}-cropped-video-transform`} sx={{ transform: `scale(${scaleX}, ${scale})` }}>
                <Box
                    id={`${props.stream}-cropped-video-crop`}
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        width: `${width - VIDEO_MARGIN}px`,
                        height: `${height - VIDEO_MARGIN}px`,
                        cursor: 'pointer',
                        borderRadius: props.applyBorderRadius ? `${10 / scale}px` : undefined,
                        '&:hover': props.applyHover ? { opacity: 0.8 } : undefined,
                    }}
                >
                    <video
                        id={props.id}
                        ref={props.videoRef}
                        src={props.src}
                        {...props.videoProps}
                        style={{
                            position: 'absolute',
                            top: `-${top + VIDEO_MARGIN}px`,
                            left: `-${left + VIDEO_MARGIN}px`,
                            ...props.videoProps.style,
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default CroppedVideo;
